import React from "react";
import { Box, Chip, Link, Stack, TableCell, TableRow, Typography } from "@mui/material";
import IconButtonIcons from "../../Common/IconButtonIcons";
import DeleteIcon from "@mui/icons-material/DeleteForever";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import TableCommon from "../../Common/Table";
import { setSnackBar } from "../../../store/common/commonSlice";
import { useDispatch } from "react-redux";
import { handleDateTimeDefault } from "../../../helpers/utils";
import { useDeleteGalleryMutation } from "../../../store/masters/mastersApis";
import { PERMISSIONS } from "../../../helpers/constants";
import HasPermission from "../../Common/HasPermission";

const PhotoGalleryTableComponent = ({
    loading,
    count,
    data,
    edit,
    pagination,
    handlePagination,
    fetching = false,
    onAssignFolder,
}) => {
    const dispatch = useDispatch();
    const [handleDelete] = useDeleteGalleryMutation();

    let columns = [
        {
            title: "Sr. No.",
            sort: false,
        },
        {
            title: "Preview",
            field: "image",
            sort: false,
        },
        {
            title: "Folder",
            field: "folder_data.name",
            sort: false,
        },
        {
            title: "Title",
            field: "title",
            sort: false,
        },
        {
            title: "Created At",
            field: "createdAt",
            sort: true,
        },
        {
            title: "Action",
            name: "",
            sort: false,
        },
    ];

    const deleteManage = async (_id) => {
        try {
            if (window.confirm("Are you sure you want to delete?")) {
                const response = await handleDelete({ _id });
                if (response?.error) throw new Error(response?.error?.message);

                dispatch(
                    setSnackBar({
                        open: true,
                        message: "Deleted successfully",
                        severity: "success",
                    }),
                );
            }
        } catch (error) {
            dispatch(
                setSnackBar({
                    open: true,
                    message: error?.data?.message || error.message,
                    severity: "error",
                }),
            );
        }
    };

    const renderTableData =
        !loading && data && data.length > 0 ? (
            data.map(function (row, index) {
                return (
                    <TableRow
                        key={index}
                        sx={{ "td, th": { borderBottom: 1, borderBottomColor: "divider", padding: "10px" } }}
                    >
                        <TableCell align="center">{index + 1 + pagination.pageNo * pagination.limit}</TableCell>
                        <TableCell align="left">
                            {row?.url?.includes("imagekit") ? (
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <Box
                                        component="img"
                                        src={row.url}
                                        alt={row?.title || "Gallery preview"}
                                        sx={{
                                            width: 56,
                                            height: 56,
                                            objectFit: "cover",
                                            borderRadius: 2,
                                            border: "1px solid",
                                            borderColor: "divider",
                                        }}
                                    />
                                    <Stack spacing={0.25}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                            {row?.title || "Photo item"}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Image upload
                                        </Typography>
                                    </Stack>
                                </Stack>
                            ) : (
                                <Stack spacing={0.5}>
                                    <Link href={row.url} target="_blank" rel="noopener noreferrer" underline="hover">
                                        {row?.title || "Open Drive Link"}
                                    </Link>
                                    <Typography variant="caption" color="text.secondary">
                                        External link
                                    </Typography>
                                </Stack>
                            )}
                        </TableCell>
                        <TableCell align="center">
                            {row?.folder_data?.name ? (
                                <Chip
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                    label={row.folder_data.name}
                                    sx={{ borderRadius: "8px" }}
                                />
                            ) : (
                                <Chip
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    label="Unassigned"
                                    sx={{ borderRadius: "8px" }}
                                />
                            )}
                        </TableCell>
                        <TableCell align="left">
                            <Typography variant="body2" color={row?.title ? "text.primary" : "text.secondary"}>
                                {row?.title || "No title"}
                            </Typography>
                        </TableCell>
                        <TableCell align="center">{handleDateTimeDefault(row?.createdAt)}</TableCell>
                        <TableCell align="center" component="th" scope="row">
                            <HasPermission permission={PERMISSIONS.COMMON_MASTER.PHOTO_GALLERY.UPDATE} fallback={null}>
                                <IconButtonIcons
                                    title={row?.folder_data?.name ? "Change Folder" : "Assign Folder"}
                                    IconComponent={DriveFileMoveOutlinedIcon}
                                    color="primary"
                                    onClick={() => onAssignFolder(row)}
                                />
                            </HasPermission>
                            <HasPermission permission={PERMISSIONS.COMMON_MASTER.PHOTO_GALLERY.DELETE} fallback={null}>
                                <IconButtonIcons
                                    title="Delete"
                                    IconComponent={DeleteIcon}
                                    color="error"
                                    onClick={() => deleteManage(row._id, row.converted)}
                                />
                            </HasPermission>
                        </TableCell>
                    </TableRow>
                );
            })
        ) : (
            <TableRow sx={{ "td, th": { border: 0, padding: "10px" } }}>
                <TableCell colSpan={6} align="center">
                    Data not found
                </TableCell>
            </TableRow>
        );

    return (
        <TableCommon
            columns={columns}
            tableData={renderTableData}
            count={count}
            loading={loading || fetching}
            pagination={pagination}
            handlePagination={handlePagination}
        />
    );
};

export default PhotoGalleryTableComponent;
